import { Input, RadioInput } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSendlaneCRUDActions } from '@mm/state/actions/SendlaneCRUDActions';
import { ConnectionModel } from '@mm/state/model/ConnectionModel';
import { selectSendlaneConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { Switch } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';

import { AudienceSimpleSelect } from '@mm/stateful-components';
import styles from './SendlaneIntegrations.module.css';
import { SendlaneListSelect } from './SendlaneListSelect';

export const SendlaneSettings = () => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const sendlaneConnections = useSelector(selectSendlaneConnections);
  const sendlaneConnection = sendlaneConnections?.[0];
  const [apiKey, setApiKey] = useState(
    sendlaneConnection?.sendlaneAccessToken?.privateKey,
  );
  const [radioValue, setRadioValue] = useState<'all' | 'audience'>(
    sendlaneConnection?.audienceId || !sendlaneConnection ? 'audience' : 'all',
  );
  const [error, setError] = useState('');

  const privateKey =
    sendlaneConnection?.sendlaneParams?.privateKey ||
    sendlaneConnection?.sendlaneAccessToken?.privateKey;

  const disabledApiKey = sendlaneConnection && apiKey === privateKey;

  const { create, update, retrieveById } = useConnectionCRUDActions();
  const { getLists } = useSendlaneCRUDActions();
  const { openNewBillingModal2023 } = useModalActions();

  const updateWebhook = async (connection: Partial<ConnectionModel>) => {
    if (!sendlaneConnection || !hasNativeIntegrationsFeature) return;
    await update(activeFbPageId, {
      id: sendlaneConnection.id,
      ...connection,
    });
  };

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.SENDLANE_UPGRADE,
      onSuccessBilling,
    });
  };

  const connectApiKey = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (!apiKey || apiKey.length < 900) {
      setError('Invalid API Key');
      return;
    }
    const params = {
      sendlaneParams: {
        privateKey: apiKey,
      },
    };

    let connection: ConnectionModel;

    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(connectApiKey);
    } else {
      if (!sendlaneConnection) {
        connection = await create(activeFbPageId, {
          name: 'Sendlane Connection',
          connectionKind: 'sendlane',
          ...params,
        });
        getLists(connection.id);
      } else {
        updateWebhook({
          ...params,
        });
      }
      setError('');
      if (sendlaneConnection?.id) {
        getLists(sendlaneConnection.id);
      }
    }
  };

  const getSendlaneConnection = async () => {
    if (sendlaneConnection) {
      const res = await retrieveById(activeFbPageId, sendlaneConnection.id);
      if (res?.sendlaneAccessToken?.status === 'error') {
        setError(
          res.sendlaneAccessToken.errorInfo ||
            'API Key error, please update it.',
        );
      }
    }
  };

  const retrieveSendlaneLists = () => {
    if (sendlaneConnection?.id && hasNativeIntegrationsFeature) {
      getLists(sendlaneConnection.id);
    }
  };

  useEffect(() => {
    getSendlaneConnection();
    retrieveSendlaneLists();
  }, []);

  return (
    <div className={styles.settingsContent}>
      <div className={classNames(styles.box, styles.boxWithHelp)}>
        <div className={styles.boxContent}>
          <div className={styles.heading}>API Key</div>
          <div className={styles.apiKeyInput}>
            <Input
              placeholder="Enter your API Key"
              css={{ container: styles.inputContainer, input: styles.input }}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
            <PrimaryButton
              className={styles.primaryButton}
              onClick={e => connectApiKey(e)}
              disabled={disabledApiKey}
            >
              {sendlaneConnection && disabledApiKey
                ? 'Connected'
                : sendlaneConnection
                ? 'Update'
                : 'Connect'}
            </PrimaryButton>
          </div>
          <div className={styles.error}>{error}</div>
        </div>
      </div>

      <div
        className={classNames(styles.box, {
          [styles.boxDisabled]:
            !sendlaneConnection?.sendlaneAccessToken ||
            !hasNativeIntegrationsFeature,
        })}
      >
        <div className={styles.boxWithHelp}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            Contact sync
            <Switch
              defaultChecked={sendlaneConnection?.active}
              checkedChildren="Yes"
              unCheckedChildren="No"
              className={styles.switch}
              onChange={ok => updateWebhook({ active: ok })}
            />
          </div>
        </div>
        <div className={styles.radioInputs}>
          <RadioInput
            name="all"
            value="all"
            label="Sync all Customers.ai contacts to Sendlane"
            onChange={() => {
              setRadioValue('all');
              updateWebhook({ audienceId: null });
            }}
            checked={radioValue === 'all'}
          />
          <RadioInput
            name="audience"
            value="audience"
            label="Choose an audience to sync from "
            onChange={() => setRadioValue('audience')}
            checked={radioValue === 'audience'}
          />
          {radioValue === 'audience' && (
            <AudienceSimpleSelect
              className={styles.audienceSelect}
              selectedAudienceId={sendlaneConnection?.audienceId || undefined}
              onChange={audienceId => updateWebhook({ audienceId })}
            />
          )}
        </div>
        <div className={styles.boxContent}>
          <div className={classNames(styles.heading, styles.fontSizeNormal)}>
            Which Sendlane list do you want to sync leads to?
          </div>

          <div className={styles.selectWrapper}>
            <SendlaneListSelect
              selectedConnection={sendlaneConnection}
              onChange={v => updateWebhook(v)}
              className={styles.listSelect}
            />
            <PrimaryButton
              className="integrations__header-add-button-secondary"
              color="transparent-no-shadow"
              onClick={retrieveSendlaneLists}
            >
              Refresh
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};
